import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import Card from 'components/containers/card'
import useTranslate from 'helpers/hooks/useTranslate'
import { Columns } from 'react-bulma-components'
import {
    DefaultButton, Label, TextField, PrimaryButton,
} from '@fluentui/react'
import DatePicker from 'components/inputs/datePicker'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import DetailInfo from 'components/pages/orders/[id]/client/common/detailInfo'
import { isValidDate } from 'helpers/methods/common'
import Param from 'requests/objects/param'
import AddressModal from 'components/pages/addressModal'
import AddressesHandler from 'requests/handlers/addressesHandler'
import EAddressType from 'types/addresses/enums/addressType'
import ContactModal from 'components/pages/contactModal'
import Address from 'requests/objects/address'
import Contact from 'requests/objects/contact'
import EWorkflowStep from 'types/orders/enums/workflowStep'
import User from 'requests/objects/user'
import OrderQuote, { ErrorOrderQuote } from 'requests/objects/order-quote'
import parseJson from 'helpers/methods/parseJson'
import MerchandiseStepContent from 'components/pages/order-quotes/[id]/merchandiseContent'
import styles from 'styles/components/pages/order-quotes/[id]/creation-step.module.scss'
import Merchandise from 'requests/objects/merchandise'
import TextFieldWithTooltip from 'components/inputs/textFieldWithTooltip'
import FilesHandler from 'requests/handlers/filesHandler'
import OrderQuoteFileList from 'components/pages/order-quotes/[id]/files/OrderQuoteFileList'
import OrderQuoteAdvancedFileInput from 'components/pages/order-quotes/[id]/files/OrderQuoteAdvancedFileInput'
import EFileFolder from 'types/files/enums/fileFolder'

/**
 * PickupStep
 * @param {object} props Props
 * @param {object} props.lang Lang
 * @param {OrderQuote} props.item Item
 * @param {User} props.me me
 * @param {ErrorOrderQuote} props.errorField errorField
 * @param {(order: Partial<OrderQuote>) => void} props.onChange onChange
 * @param {Param} props.param param
 * @param {AddressesHandler} props.addressesHandler AddressesHandler
 * @param {(arg: import('redux/slices/user').PayloadParam) => void} props.editParam editParam
 * @param {(arg: import('redux/slices/user').PayloadParam) => void} props.addParam addParam
 * @param {(arg: import('redux/slices/user').PayloadParam) => void} props.removeParam removeParam
 * @param {boolean} props.isReadOnly isReadOnly
 * @param {Param}props.handler param
 * @returns {JSX.Element} Element
 */
export default function CreationStep({
    lang, item, me, errorField, onChange, param, addressesHandler, editParam, addParam, removeParam,
    isReadOnly, handler,
}) {
    const { tKey, tObj } = useTranslate({ lang })
    const [isAddressModalVisible, setIsAddressModalVisible] = useState([false, EAddressType.PickUp])
    const [isContactModalVisible, setIsContactModalVisible] = useState([false, EAddressType.PickUp])
    const [addressModalData, setAddressModalData] = useState(new Address({}))

    const pickupStepErrorField = useMemo(() => errorField, [errorField])

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<Address>>} */
    const getAddressHandler = useRef()

    // fetch address  data
    const fetchAddress = useCallback(async addressId => {
        if (!addressId)
            return null

        try {
            getAddressHandler.current = addressesHandler.getById(addressId)
            const address = await getAddressHandler.current.fetch()

            return address
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
            return null
        }
    }, [addressesHandler])

    const setAddressDataById = useCallback(async (addressId, type) => {
        try {
            if (!addressId) {
                if (type === EAddressType.Delivery)
                    onChange({
                        ...item,
                        deliveryAddress: null,
                        deliveryAddressId: 0,
                    })

                else if (type === EAddressType.PickUp)
                    onChange({
                        ...item,
                        pickupAddress: null,
                        pickupAddressId: 0,
                    })
                return
            }
            // setStatus(Status.PENDING)
            getAddressHandler.current = addressesHandler.getById(addressId)
            const address = await getAddressHandler.current.fetch()

            if (type === EAddressType.Delivery)
                onChange({
                    ...item,
                    deliveryAddress: address,
                    deliveryAddressId: address.addressId,
                })

            else if (type === EAddressType.PickUp)
                onChange({
                    ...item,
                    pickupAddress: address,
                    pickupAddressId: address.addressId,
                })
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
        }
    }, [addressesHandler, item, onChange])

    useEffect(() => () => {
        getAddressHandler?.current?.cancel()
    }, [])

    const filesHandler = useMemo(() => new FilesHandler(), [])

    const normalizeMerchandises = merchandises => merchandises.map(m => {
        const calcFootage = m.article?.calcFootage ?? true

        return {
            ...m,
            calcFootage,
            manualFootage: false,
        }
    })
    useEffect(() => {
        if (!isReadOnly && item?.merchandises?.length) {
            const normalized = normalizeMerchandises(item.merchandises)

            if (JSON.stringify(normalized) !== JSON.stringify(item.merchandises))
                onChange({ merchandises: normalized })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReadOnly])

    return (
        <>
            <div className={styles['order-quote']}>
                <div className={styles['order-quote-pickup']}>
                    <Card
                        title={tKey('pickup')}
                        iconName="CityNext"
                        hasLitleDividerMarging
                    >
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('pickupAddress')}
                                    placeholder={tKey('research')}
                                    options={param.addresses}
                                    selectedKey={item.pickupAddressId}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, option) => fetchAddress(option.key).then(address => onChange({
                                        pickupAddressId: address?.addressId ?? 0,
                                        pickupAddress: address,
                                    }))}
                                    required={!isReadOnly}
                                    addButtonProps={{
                                        addName: tKey('addAddress'),
                                        onAddClick: () => {
                                            setAddressModalData(new Address({
                                                addressTypeId: EAddressType.PickUp,
                                                startTime: '00:00',
                                                endTime: '23:59',
                                            }))
                                            setIsAddressModalVisible([true, EAddressType.PickUp])
                                        },
                                    }}
                                />
                                {item.pickupAddress?.addressId && (
                                    <DetailInfo
                                        isReadOnly={isReadOnly}
                                        lang={lang}
                                    >
                                        <b>{item.pickupAddress.label}</b>
                                        <br />
                                        {[
                                            item.pickupAddress.street,
                                            item.pickupAddress.zipCode,
                                            item.pickupAddress.city,
                                            item.pickupAddress.country?.code,
                                        ].filter(x => x).join(', ')}
                                    </DetailInfo>
                                )}
                            </Columns.Column>
                            {!!item.pickupAddress && (
                                <Columns.Column size="half">
                                    {
                                        // eslint-disable-next-line no-nested-ternary
                                        item.pickupAddress?.contact?.contactId ? (
                                            <DetailInfo
                                                onEdit={() => {
                                                    setIsContactModalVisible([true, EAddressType.PickUp])
                                                }}
                                                label={tKey('contact')}
                                                isReadOnly={isReadOnly}
                                                lang={lang}
                                            >
                                                <span style={{ fontWeight: 'bold' }}>
                                                    {
                                                        // eslint-disable-next-line max-len
                                                        `${item.pickupAddress.contact.firstname} ${item.pickupAddress.contact.lastname.toUpperCase()}`
                                                    }
                                                </span>
                                                <br />
                                                {item.pickupAddress.contact.email}
                                                <br />
                                                {!!item.pickupAddress.contact.phoneNumber && `+${item.pickupAddress.contact.phoneNumber}`}
                                                <br />
                                                {!!item.pickupAddress.contact.phoneNumber2 && `+${item.pickupAddress.contact.phoneNumber2}`}
                                            </DetailInfo>
                                        ) : !isReadOnly ? (
                                            <>
                                                <Label
                                                    required
                                                    styles={{ root: { paddingBottom: 0, marginBottom: 0 } }}
                                                >
                                                    {tKey('contact')}
                                                </Label>
                                                <TextField
                                                    styles={{
                                                        fieldGroup: {
                                                            border: 'none', padding: 0, margin: 0, height: 0,
                                                        },
                                                    }}
                                                    errorMessage={tObj(pickupStepErrorField['item.pickupAddressId'])}
                                                />
                                                <PrimaryButton
                                                    style={{ marginTop: '10px' }}
                                                    text={tKey('addContact')}
                                                    disabled={isReadOnly}
                                                    onClick={() => {
                                                        onChange({
                                                            pickupAddress: {
                                                                ...item.pickupAddress,
                                                                contact: new Contact({ addressId: item.pickupAddressId }),
                                                            },
                                                        })
                                                        setIsContactModalVisible([true, EAddressType.PickUp])
                                                    }}
                                                    iconProps={{ iconName: 'Add' }}
                                                />
                                            </>
                                        ) : null
                                    }
                                </Columns.Column>
                            )}
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <DatePicker
                                    date={item.pickupStartDate}
                                    label={tKey('pickupDate')}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChangeDate={newDate => onChange({
                                        pickupStartDate: isValidDate(new Date(newDate)) ? /** @type {any} */(new Date(newDate)) : '',
                                    })}
                                    errorMessage={errorField.pickupStartDate ? tKey('missingDate') : ''}
                                    required={!isReadOnly}
                                    minDate={new Date()}
                                    lang={lang}
                                />
                            </Columns.Column>
                            <Columns.Column size="half" />
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('vehicleEquipment')}
                                    placeholder={tKey('select')}
                                    options={param.vehicleEquipments.map(x => ({ ...x, text: tObj(parseJson(x.text)) }))}
                                    readOnly={isReadOnly}
                                    selectedKey={item.pickupVehicleEquipmentId}
                                    onChange={(ev, option) => onChange({ pickupVehicleEquipmentId: option.key })}
                                    errorMessage={tObj(errorField.pickupVehicleEquipmentId)}
                                />
                            </Columns.Column>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('accessibility')}
                                    placeholder={tKey('select')}
                                    options={param.accessibilities.map(x => ({ ...x, text: tObj(parseJson(x.text)) }))}
                                    readOnly={isReadOnly}
                                    selectedKey={item.pickupAccessibilityId}
                                    onChange={(ev, option) => onChange({ pickupAccessibilityId: option.key })}
                                    errorMessage={tObj(errorField.pickupAccessibilityId)}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <TextFieldWithTooltip
                                    label={tKey('pickupRequirement')}
                                    placeholder={isReadOnly ? tKey('noRequirement') : tKey('writeAnImperative')}
                                    value={item.pickupRequirement ?? ''}
                                    onChange={(_ev, newVal) => onChange({
                                        pickupRequirement: newVal ?? '',
                                    })}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    multiline
                                    autoAdjustHeight={!isReadOnly}
                                    resizable={false}
                                />
                            </Columns.Column>
                        </Columns>
                    </Card>
                </div>
                <div className={styles['order-quote-delivery']}>
                    <Card
                        title={tKey('delivery')}
                        iconName="CityNext"
                        hasLitleDividerMarging
                    >
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('deliveryAddress')}
                                    placeholder={tKey('research')}
                                    options={param.addresses}
                                    selectedKey={item.deliveryAddressId}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChange={(ev, option) => fetchAddress(option.key).then(address => onChange({
                                        deliveryAddressId: address?.addressId ?? 0,
                                        deliveryAddress: address,
                                    }))}
                                    required={!isReadOnly}
                                    addButtonProps={{
                                        addName: tKey('addAddress'),
                                        onAddClick: () => {
                                            setAddressModalData(new Address({
                                                addressTypeId: EAddressType.Delivery,
                                                startTime: '00:00',
                                                endTime: '23:59',
                                            }))
                                            setIsAddressModalVisible([true, EAddressType.Delivery])
                                        },
                                    }}
                                />
                                {item.deliveryAddress?.addressId && (
                                    <DetailInfo
                                        onEdit={() => {
                                            setAddressModalData(new Address({ addressId: item.deliveryAddress.addressId }))
                                            setIsAddressModalVisible([true, EAddressType.Delivery])
                                        }}
                                        isReadOnly={isReadOnly}
                                        lang={lang}
                                    >
                                        <b>{item.deliveryAddress.label}</b>
                                        <br />
                                        {[
                                            item.deliveryAddress.street,
                                            item.deliveryAddress.zipCode,
                                            item.deliveryAddress.city,
                                            item.deliveryAddress.country?.code,
                                        ].filter(x => x).join(', ')}
                                    </DetailInfo>
                                )}
                            </Columns.Column>
                            {!!item.deliveryAddress && (
                                <Columns.Column size="half">
                                    {
                                        // eslint-disable-next-line no-nested-ternary
                                        item.deliveryAddress?.contact?.contactId ? (
                                            <DetailInfo
                                                onEdit={() => setIsContactModalVisible([true, EAddressType.Delivery])}
                                                label={tKey('contact')}
                                                isReadOnly={isReadOnly}
                                                lang={lang}
                                            >
                                                <span style={{ fontWeight: 'bold' }}>
                                                    {
                                                        // eslint-disable-next-line max-len
                                                        `${item.deliveryAddress.contact.firstname} ${item.deliveryAddress.contact.lastname.toUpperCase()}`
                                                    }
                                                </span>
                                                <br />
                                                {item.deliveryAddress.contact.email}
                                                <br />
                                                {!!item.deliveryAddress.contact.phoneNumber && `+${item.deliveryAddress.contact.phoneNumber}`}
                                                <br />
                                                {!!item.deliveryAddress.contact.phoneNumber2 && `+${item.deliveryAddress.contact.phoneNumber2}`}
                                            </DetailInfo>
                                        ) : !isReadOnly ? (
                                            <>
                                                <Label
                                                    required
                                                    styles={{ root: { paddingBottom: 0, marginBottom: 0 } }}
                                                >
                                                    {tKey('contact')}
                                                </Label>
                                                <TextField
                                                    styles={{
                                                        fieldGroup: {
                                                            border: 'none', padding: 0, margin: 0, height: 0,
                                                        },
                                                    }}
                                                    errorMessage={tObj(errorField.deliveryAddressId)}
                                                />
                                                <PrimaryButton
                                                    style={{ marginTop: '10px' }}
                                                    text={tKey('addContact')}
                                                    disabled={isReadOnly}
                                                    onClick={() => {
                                                        onChange({
                                                            deliveryAddress: {
                                                                ...item.deliveryAddress,
                                                                contact: new Contact({ addressId: item.deliveryAddressId }),
                                                            },
                                                        })
                                                        setIsContactModalVisible([true, EAddressType.Delivery])
                                                    }}
                                                    iconProps={{ iconName: 'Add' }}
                                                />
                                            </>
                                        ) : null
                                    }
                                </Columns.Column>
                            )}
                        </Columns>

                        <Columns>
                            <Columns.Column size="half">
                                <DatePicker
                                    date={item.deliveryStartDate}
                                    label={tKey('startDeliveryDate')}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChangeDate={newDate => onChange({
                                        deliveryStartDate: isValidDate(new Date(newDate)) ? /** @type {any} */(new Date(newDate)) : '',
                                    })}
                                    errorMessage={errorField.deliveryStartDate ? tKey('missingDate') : ''}
                                    required={!isReadOnly}
                                    minDate={new Date()}
                                    lang={lang}
                                />
                            </Columns.Column>
                            <Columns.Column size="half">
                                <DatePicker
                                    date={item.deliveryEndDate}
                                    label={tKey('endDeliveryDate')}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    onChangeDate={newDate => onChange({
                                        deliveryEndDate: isValidDate(new Date(newDate)) ? /** @type {any} */(new Date(newDate)) : '',
                                    })}
                                    errorMessage={errorField.deliveryEndDate ? tKey('missingDate') : ''}
                                    required={!isReadOnly}
                                    minDate={new Date()}
                                    lang={lang}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('vehicleEquipment')}
                                    placeholder={tKey('select')}
                                    options={param.vehicleEquipments.map(x => ({ ...x, text: tObj(parseJson(x.text)) }))}
                                    readOnly={isReadOnly}
                                    selectedKey={item.deliveryVehicleEquipmentId}
                                    onChange={(ev, option) => onChange({ deliveryVehicleEquipmentId: option.key })}
                                    errorMessage={tObj(errorField.deliveryVehicleEquipmentId)}
                                />
                            </Columns.Column>
                            <Columns.Column size="half">
                                <FilteredVirtualCombobox
                                    label={tKey('accessibility')}
                                    placeholder={tKey('select')}
                                    options={param.accessibilities.map(x => ({ ...x, text: tObj(parseJson(x.text)) }))}
                                    readOnly={isReadOnly}
                                    selectedKey={item.deliveryAccessibilityId}
                                    onChange={(ev, option) => onChange({ deliveryAccessibilityId: option.key })}
                                    errorMessage={tObj(errorField.deliveryAccessibilityId)}
                                />
                            </Columns.Column>
                        </Columns>
                        <Columns>
                            <Columns.Column size="half">
                                <TextFieldWithTooltip
                                    label={tKey('deliveryRequirement')}
                                    placeholder={isReadOnly ? tKey('noRequirement') : tKey('writeAnImperative')}
                                    value={item.deliveryRequirement ?? ''}
                                    onChange={(_ev, newVal) => onChange({
                                        deliveryRequirement: newVal ?? '',
                                    })}
                                    readOnly={isReadOnly}
                                    borderless={isReadOnly}
                                    multiline
                                    autoAdjustHeight={!isReadOnly}
                                    resizable={false}
                                />
                            </Columns.Column>
                        </Columns>
                    </Card>
                </div>
                <div className={styles['order-quote-goods']}>
                    <Card
                        title={tKey('content')}
                        iconName="ShoppingCart"
                        hasLitleDividerMarging
                    >
                        <div className={styles['delivery-step-right-content-content']}>
                            <MerchandiseStepContent
                                handler={handler}
                                items={item.merchandises}
                                lang={lang}
                                onUpdate={newVal => onChange({ merchandises: newVal })}
                                param={param}
                                isReadOnly={isReadOnly}
                                errorField={errorField}
                                isFootageInformable={me?.client?.isFootageInformable}
                            />
                            {!isReadOnly && (
                                <DefaultButton
                                    text={tKey('addHandlingUnit')}
                                    className={styles['btns-blue-btn']}
                                    iconProps={{ iconName: 'Add' }}
                                    onClick={() => onChange({
                                        merchandises: [...item.merchandises, new Merchandise({
                                            quantity: 1,
                                            isManualArticle: false,
                                            articleId: 0,
                                            merchandiseName: '',
                                            weight: null,
                                        })],
                                    })}
                                />
                            )}
                        </div>
                    </Card>
                </div>
                <div className={styles['order-quote-complement']}>
                    <Card
                        title={tKey('complement')}
                        iconName="TextBox"
                        hasLitleDividerMarging

                    >
                        <TextFieldWithTooltip
                            label={tKey('orderingPartyReference')}
                            placeholder={isReadOnly ? '' : tKey('referenceNumber')}
                            value={item.orderingPartyReference}
                            onChange={(_ev, newVal) => {
                                onChange({ orderingPartyReference: newVal?.length > 100 ? newVal.substring(0, 100) : newVal })
                            }}
                            errorMessage={tObj(errorField.orderingPartyReference)}
                            multiline
                            autoAdjustHeight={!isReadOnly}
                            readOnly={isReadOnly}
                            borderless={isReadOnly}
                            resizable={false}
                            tooltipMessage={tKey('orderingPartyReferenceTooltip')}
                        />
                    </Card>
                </div>
                <div className={styles['order-quote-documents']}>
                    <Card
                        title={tKey('documents')}
                        iconName="DocumentSet"
                        hasLitleDividerMarging
                    >
                        <Columns>
                            <Columns.Column size="half">
                                <OrderQuoteFileList
                                    item={item}
                                    files={item.files}
                                    isReadOnly={isReadOnly}
                                    lang={lang}
                                    removeFile={fileId => onChange({
                                        files: item.files.filter(x => x.fileId !== fileId),
                                    })}
                                    handler={filesHandler}
                                />

                                {isReadOnly && item.files.length === 0 && (
                                    <TextField
                                        borderless
                                        readOnly
                                        placeholder={tKey('noDocument')}
                                    />
                                )}
                            </Columns.Column>

                            <Columns.Column size="half">
                                {!isReadOnly && (
                                    <OrderQuoteAdvancedFileInput
                                        lang={lang}
                                        handler={filesHandler}
                                        entityId={item.orderQuoteId}
                                        fileFolderId={EFileFolder.OrderQuoteDocument}
                                        options={param.fileCategories}
                                        isReadOnly={isReadOnly}
                                        allowUrlUpload
                                        fileToken={item.fileToken}
                                        addFile={newFile => {
                                            if (!newFile)
                                                return
                                            onChange({
                                                files: [...(item.files ?? []).filter(Boolean), newFile].filter(Boolean),
                                            })
                                        }}
                                    />
                                )}
                            </Columns.Column>
                        </Columns>
                    </Card>
                </div>
            </div>
            <AddressModal
                addressData={addressModalData}
                handler={addressesHandler}
                isVisible={isAddressModalVisible[0]}
                lang={lang}
                addParam={addParam}
                editParam={editParam}
                removeParam={removeParam}
                param={param}
                onChange={newData => {
                    if (newData)
                        setAddressDataById(newData.addressId, isAddressModalVisible[1])
                    setIsAddressModalVisible([false])
                }}
                companyRules={me.company.companyRule}
                isValidation={item.currentWorkflowStepId === EWorkflowStep.AddressValidation}
                addressCanBeOptional={false}
            />
            <ContactModal
                isVisible={isContactModalVisible[0]}
                handler={addressesHandler}
                contactData={isContactModalVisible[1] === EAddressType.PickUp ? item.pickupAddress?.contact : item.deliveryAddress?.contact}
                lang={lang}
                onChange={newData => {
                    if (newData)
                        if (isContactModalVisible[1] === EAddressType.PickUp)
                            onChange({
                                pickupAddress: { ...item.pickupAddress, contact: newData },
                            })
                        else if (isContactModalVisible[1] === EAddressType.Delivery)
                            onChange({
                                deliveryAddress: { ...item.deliveryAddress, contact: newData },
                            })

                    setIsContactModalVisible([false])
                }}
                companyId={me.companyId}
            />
        </>
    )
}
